import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { useParams } from 'react-router-dom';

import PopoverTooltip from 'App/components/Popovers/PopoverTooltip';
import FlexCenter from 'App/components/styles/FlexCenter';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import AssessmentService from 'App/services/AssessmentService';
import CancelButton from 'Shared/form/CancelButton';
import { NumberField } from 'Shared/form/NumberField';
import SubmitButton from 'Shared/form/SubmitButton';
import { TextField } from 'Shared/form/TextField';
import { TextareaField } from 'Shared/form/TextareaField';
import { queryClient } from 'Shared/queryClient';
import useFetchAssessmentRiskDetails from './hooks/useFetchAssessmentRiskDetails';
import assessmentRiskPropType from './types/assessmentRiskPropType';

const AssessmentManagerScoringModal = ({ risk, onClose }) => {
    const { assessmentId } = useParams();
    const { data, isLoading } = useFetchAssessmentRiskDetails(assessmentId, risk?.id);

    if (isLoading) {
        return null;
    }

    return (
        <Modal show size="lg" onHide={onClose}>
            <Modal.Header>
                <Modal.Title role="heading">{risk ? 'Edit Scoring' : 'Create Scoring'}</Modal.Title>
            </Modal.Header>

            <Formik
                initialValues={{
                    ...data,
                    ranges: data?.ranges || [{}],
                }}
                onSubmit={async (values) => {
                    const payload = { ...values };
                    if (!risk) {
                        payload.is_new = true;
                    }
                    await AssessmentService.saveRisk(assessmentId, payload);
                    queryClient.invalidateQueries(['assessmentId', assessmentId, 'riskId', risk?.id, 'details']);
                    queryClient.refetchQueries(['assessmentId', assessmentId, 'riskOptions']);
                    return onClose();
                }}
            >
                {({ handleSubmit, values, setFieldValue, validateForm }) => (
                    <form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col md={6}>
                                    <TextField fieldName="name" placeholder="Name" label="Name" required />
                                </Col>
                            </Row>

                            <TextareaField
                                fieldName="description"
                                placeholder="Description"
                                label="Description"
                                required
                            />

                            {values.ranges.map((range, index) => (
                                <FlexCenter key={range.id || `new-range-index-${index}`}>
                                    <Row style={{ flexGrow: 10 }}>
                                        <Col md={3}>
                                            <TextField
                                                fieldName={`ranges[${index}].value`}
                                                required
                                                placeholder="Stratification"
                                                label={
                                                    <>
                                                        Stratification
                                                        <span className="float-right">
                                                            <PopoverTooltip
                                                                tooltipContent="Label for range to indicate risk of non-compliance."
                                                                tooltipId="stratification-popover"
                                                            />
                                                        </span>
                                                    </>
                                                }
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <NumberField
                                                fieldName={`ranges[${index}].range_start`}
                                                required
                                                placeholder="Range Start"
                                                label={
                                                    <>
                                                        Range Start
                                                        <span className="float-right">
                                                            <PopoverTooltip
                                                                tooltipContent="Beginning score for range. Do not overlap Range Start and Range End when using multiple ranges."
                                                                tooltipId="range-start-popover"
                                                            />
                                                        </span>
                                                    </>
                                                }
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <NumberField
                                                fieldName={`ranges[${index}].range_end`}
                                                required
                                                placeholder="Range End"
                                                label={
                                                    <>
                                                        Range End
                                                        <span className="float-right">
                                                            <PopoverTooltip
                                                                tooltipContent="Ending score for range. Do not overlap Range Start and Range End when using multiple ranges."
                                                                tooltipId="range-end-popover"
                                                            />
                                                        </span>
                                                    </>
                                                }
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <NumberField
                                                fieldName={`ranges[${index}].follow_up_date`}
                                                min={0}
                                                placeholder="Follow Up Days"
                                                label={
                                                    <>
                                                        Follow Up Days
                                                        <span className="float-right">
                                                            <PopoverTooltip
                                                                tooltipContent="Number of calendar days for follow up. This will be converted to a date within the activity."
                                                                tooltipId="follow-up-date-popover"
                                                            />
                                                        </span>
                                                    </>
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <div>
                                        <Button
                                            aria-label="Remove Range"
                                            variant="link"
                                            disabled={values.ranges.length <= 1}
                                            onClick={() => {
                                                const newRanges = [...values.ranges];
                                                newRanges.splice(index, 1);
                                                setFieldValue('ranges', newRanges);
                                                setImmediate(() => validateForm());
                                            }}
                                        >
                                            <FaTimes />
                                        </Button>
                                    </div>
                                </FlexCenter>
                            ))}
                            <Button
                                variant="link"
                                onClick={() => {
                                    const newIndex = values.ranges.length;
                                    setFieldValue(`ranges[${newIndex}]`, {});
                                    setImmediate(() => validateForm());
                                }}
                            >
                                <FaPlus /> Add Range
                            </Button>
                        </Modal.Body>

                        <Modal.Footer>
                            <FormButtonGroup>
                                {risk?.company_id && (
                                    <Button
                                        variant="danger"
                                        onClick={async () => {
                                            await AssessmentService.deleteRisk(assessmentId, risk.id);
                                            queryClient.refetchQueries(['assessmentId', assessmentId, 'riskOptions']);
                                            onClose();
                                        }}
                                    >
                                        Delete
                                    </Button>
                                )}
                                <CancelButton onClick={onClose} />
                                <SubmitButton />
                            </FormButtonGroup>
                        </Modal.Footer>
                    </form>
                )}
            </Formik>
        </Modal>
    );
};

AssessmentManagerScoringModal.propTypes = {
    risk: assessmentRiskPropType,
    onClose: PropTypes.func.isRequired,
};
AssessmentManagerScoringModal.defaultProps = {
    risk: null,
};

export default AssessmentManagerScoringModal;
